import type {
  FetchZoneDefinitionListParams,
  ZoneDefinition
} from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  ZONE_DEFINITION = 'ZoneDefinition'
}

export const zoneDefinitionApi = createBaseApi({
  reducerPath: 'zoneDefinitionApi',
  tagTypes: [TagTypes.ZONE_DEFINITION]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getZoneDefinitions: builder.query<ZoneDefinition[], FetchZoneDefinitionListParams | void>({
      query: params => ({
        url: '/api/v1/zone-definition/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.ZONE_DEFINITION, id }) as const),
              { type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }],
      transformResponse: (response: { resources: ZoneDefinition[] }) => response.resources ?? []
    }),
    getSingleZoneDefinition: builder.query<ZoneDefinition, string>({
      query: id => `/api/v1/zone-definition/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.ZONE_DEFINITION, id }]
    }),
    createSingleZoneDefinition: builder.mutation<ZoneDefinition, Partial<ZoneDefinition>>({
      query: data => ({
        url: `/api/v1/zone-definition/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }]
    }),
    updateSingleZoneDefinition: builder.mutation<ZoneDefinition, Partial<ZoneDefinition>>({
      query: data => ({
        url: `/api/v1/zone-definition/${data.id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: TagTypes.ZONE_DEFINITION, id }]
    }),
    deleteSingleZoneDefinition: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/zone-definition/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: TagTypes.ZONE_DEFINITION, id: RTKCacheTags.LIST }]
    }),
    getSingleZoneDefinitionByPta: builder.query<ZoneDefinition, void>({
      query: () => ({
        url: `/api/v1/zone-definition/by-user-pta/`,
        method: 'GET'
      })
    })
  })
})

export const {
  useGetZoneDefinitionsQuery,
  useGetSingleZoneDefinitionQuery,
  useCreateSingleZoneDefinitionMutation,
  useUpdateSingleZoneDefinitionMutation,
  useDeleteSingleZoneDefinitionMutation,
  useGetSingleZoneDefinitionByPtaQuery
} = zoneDefinitionApi
